// src/data/notifications.js

const NOTIFICATION_KEY = 'bigstar_notifications';

export const NotificationTypes = {
  UNIT_ADDED:          'unit_added',
  UNIT_ASSIGNED:       'unit_assigned',
  UNIT_CANCELLED:      'unit_cancelled',
  CONTAINER_CREATED:   'container_created',
  CONTAINER_LOADED:    'container_loaded',
  CONTAINER_ARRIVED:   'container_arrived',
  REQUEST_NEW:         'request_new',
  REQUEST_UPDATED:     'request_updated',
  CUSTOMER_ADDED:      'customer_added',
  PRICE_UPDATED:       'price_updated',
  USER_LOGIN:          'user_login',
};

export const NotificationIcons = {
  unit_added:          '📦',
  unit_assigned:       '🏷️',
  unit_cancelled:      '🚫',
  container_created:   '🚢',
  container_loaded:    '📥',
  container_arrived:   '✅',
  request_new:         '💬',
  request_updated:     '📝',
  customer_added:      '👤',
  price_updated:       '💰',
  user_login:          '🔐',
};

export const Notifications = {
  getAll: () => {
    try {
      return JSON.parse(localStorage.getItem(NOTIFICATION_KEY)) || [];
    } catch {
      return [];
    }
  },

  // Get notifications visible to a specific user:
  // - customers see only notifications targeted at their ID
  // - staff see only global notifications (no targetUserId)
  // - accountants only see unit_added from uploader or japan_yard
  getForUser: (userId, isCustomer = false, userRole = null) => {
    const all = Notifications.getAll();
    if (isCustomer) {
      return all.filter(n => n.targetUserId === userId);
    }

    // Admin / head_admin: see everything
    if (userRole === 'admin' || userRole === 'head_admin' || !userRole) {
      return all.filter(n => !n.targetUserId);
    }

    // Start with notifications targeted to this user specifically
    let visible = all.filter(n => n.targetUserId === userId);

    // Accountant: money-related only — pricing, invoices, payments, containers, new units
    if (userRole === 'accountant') {
      const accountantTypes = ['price_updated', 'invoice_created', 'payment_received', 'payment_verified',
        'unit_added', 'container_shipped', 'container_loaded', 'container_arrived', 'selection_approved',
        'selection_submitted', 'quote_approved', 'quote_submitted'];
      const extra = all.filter(n => !n.targetUserId && (
        accountantTypes.includes(n.type) ||
        (n.title || '').includes('Price') || (n.title || '').includes('Invoice') ||
        (n.title || '').includes('Payment') || (n.title || '').includes('Quote') ||
        (n.title || '').includes('FOB') || (n.title || '').includes('Pricing')
      ));
      visible = [...visible, ...extra];
    }

    // Yard sales: yard-related only
    else if (userRole === 'yard_sales') {
      const extra = all.filter(n => !n.targetUserId && (
        (n.title || '').includes('Sale') || (n.title || '').includes('Hold') ||
        (n.title || '').includes('Bid') || (n.title || '').includes('Walk-in') ||
        (n.title || '').includes('Condition') || (n.title || '').includes('Task') ||
        n.type === 'yard_sale' || n.type === 'hold_request'
      ));
      visible = [...visible, ...extra];
    }

    // Japan yard: inventory, containers, cut plans
    else if (userRole === 'japan_yard') {
      const jpTypes = ['unit_added', 'container_shipped', 'container_loaded', 'container_arrived',
        'cut_plan_approved', 'cut_plan_approval_needed'];
      const extra = all.filter(n => !n.targetUserId && (
        jpTypes.includes(n.type) ||
        (n.title || '').includes('Cut Plan') || (n.title || '').includes('Container') ||
        (n.title || '').includes('Unit') || (n.title || '').includes('Task')
      ));
      visible = [...visible, ...extra];
    }

    // Data entry / uploader: only unit-related + tasks
    else if (userRole === 'uploader') {
      const extra = all.filter(n => !n.targetUserId && (
        n.type === 'unit_added' || (n.title || '').includes('Task')
      ));
      visible = [...visible, ...extra];
    }

    // Destination: only container/shipping related
    else if (userRole === 'destination') {
      const extra = all.filter(n => !n.targetUserId && (
        (n.title || '').includes('Container') || (n.title || '').includes('Shipped') ||
        (n.title || '').includes('Arrived') || (n.title || '').includes('Delivery') ||
        n.type === 'container_shipped' || n.type === 'container_arrived'
      ));
      visible = [...visible, ...extra];
    }

    // Web content: nothing extra (CMS only)
    else if (userRole === 'web_content') {
      // Only targeted notifications
    }

    // Deduplicate
    const seen = new Set();
    return visible.filter(n => { if (seen.has(n.id)) return false; seen.add(n.id); return true; });
  },

  getUnread: () => {
    return Notifications.getAll().filter(n => !n.read);
  },

  getUnreadCount: () => {
    return Notifications.getUnread().length;
  },

  getUnreadCountForUser: (userId, isCustomer = false) => {
    return Notifications.getForUser(userId, isCustomer).filter(n => !n.read).length;
  },

  // Language-aware notification for customers — uses templates from notificationMessages.js
  // Falls back to English if template or language not found
  addForCustomer: (type, templateKey, params, customerId) => {
    try {
      const { getNotificationContent, getCustomerLanguage } = require('../utils/notificationMessages');
      const lang = getCustomerLanguage(customerId);
      const content = getNotificationContent(templateKey, lang, params);
      if (content) {
        const notif = Notifications.add(type, content.title, content.message, { templateKey, lang }, customerId);
        // Generate WhatsApp link if template has whatsapp text
        if (content.whatsapp && notif) {
          const customers = JSON.parse(localStorage.getItem('bigstar_customers') || '[]');
          const cust = customers.find(c => c.id === customerId);
          if (cust?.phone) {
            const digits = (cust.phone || '').replace(/[^\d]/g, '');
            if (digits.length >= 7) {
              const all = Notifications.getAll();
              const upd = all.map(n => n.id === notif.id ? { ...n, whatsapp_link: `https://wa.me/${digits}?text=${encodeURIComponent(content.whatsapp)}` } : n);
              try { localStorage.setItem(NOTIFICATION_KEY, JSON.stringify(upd)); } catch {}
            }
          }
        }
        return notif;
      }
    } catch {}
    // Fallback to plain English
    return Notifications.add(type, params._fallbackTitle || templateKey, params._fallbackMessage || '', {}, customerId);
  },

  // targetUserId: null = visible to staff only; a customer ID = visible to that customer only
  add: (type, title, message, metadata = {}, targetUserId = null) => {
    const notifications = Notifications.getAll();
    const newNotification = {
      id:          `notif_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      type,
      title,
      message,
      metadata,
      targetUserId,
      read:        false,
      createdAt:   new Date().toISOString(),
    };

    // Keep only last 100 notifications total
    const updated = [newNotification, ...notifications].slice(0, 100);
    localStorage.setItem(NOTIFICATION_KEY, JSON.stringify(updated));

    window.dispatchEvent(new CustomEvent('notificationAdded', { detail: newNotification }));

    return newNotification;
  },

  markAsRead: (id) => {
    const notifications = Notifications.getAll();
    const updated = notifications.map(n => n.id === id ? { ...n, read: true } : n);
    localStorage.setItem(NOTIFICATION_KEY, JSON.stringify(updated));
    window.dispatchEvent(new CustomEvent('notificationsUpdated'));
  },

  markAllAsRead: () => {
    const notifications = Notifications.getAll();
    const updated = notifications.map(n => ({ ...n, read: true }));
    localStorage.setItem(NOTIFICATION_KEY, JSON.stringify(updated));
    window.dispatchEvent(new CustomEvent('notificationsUpdated'));
  },

  markAllReadForUser: (userId, isCustomer = false) => {
    const notifications = Notifications.getAll();
    const updated = notifications.map(n => {
      if (isCustomer && n.targetUserId === userId) return { ...n, read: true };
      if (!isCustomer && !n.targetUserId) return { ...n, read: true };
      return n;
    });
    localStorage.setItem(NOTIFICATION_KEY, JSON.stringify(updated));
    window.dispatchEvent(new CustomEvent('notificationsUpdated'));
  },

  delete: (id) => {
    const notifications = Notifications.getAll();
    const updated = notifications.filter(n => n.id !== id);
    localStorage.setItem(NOTIFICATION_KEY, JSON.stringify(updated));
    window.dispatchEvent(new CustomEvent('notificationsUpdated'));
  },

  clearAll: () => {
    localStorage.setItem(NOTIFICATION_KEY, JSON.stringify([]));
    window.dispatchEvent(new CustomEvent('notificationsUpdated'));
  },

  clearForUser: (userId, isCustomer = false) => {
    const all = Notifications.getAll();
    const updated = all.filter(n => {
      if (isCustomer) return n.targetUserId !== userId;
      return !!n.targetUserId; // remove global (staff) ones
    });
    localStorage.setItem(NOTIFICATION_KEY, JSON.stringify(updated));
    window.dispatchEvent(new CustomEvent('notificationsUpdated'));
  },

  timeAgo: (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60)     return 'Just now';
    if (seconds < 3600)   return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400)  return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  }
};

// Activity logger
export const logActivity = {
  unitAdded: (unitType, unitName, addedBy, actorRole = null) => {
    Notifications.add(
      NotificationTypes.UNIT_ADDED,
      `New ${unitType} Added`,
      `${unitName} was added to inventory by ${addedBy}`,
      { unitType, unitName, addedBy, actorRole }
    );
  },

  // Fire two notifications: one for the customer, one global for staff
  unitAssigned: (unitName, customerName, assignedBy, targetCustomerId = null) => {
    if (targetCustomerId) {
      // Customer-targeted notification
      Notifications.add(
        NotificationTypes.UNIT_ASSIGNED,
        'New Unit Assigned to You',
        `${unitName} has been assigned to your account by ${assignedBy}`,
        { unitName, customerName, assignedBy },
        targetCustomerId
      );
    }
    // Global staff notification
    Notifications.add(
      NotificationTypes.UNIT_ASSIGNED,
      'Unit Assigned',
      `${unitName} assigned to ${customerName}${assignedBy ? ` by ${assignedBy}` : ''}`,
      { unitName, customerName, assignedBy }
    );
  },

  // Customer cancels an assignment — staff global notification
  unitCancelled: (unitName, customerName, reason) => {
    Notifications.add(
      NotificationTypes.UNIT_CANCELLED,
      'Assignment Cancelled by Customer',
      `${customerName} cancelled "${unitName}". Reason: ${reason}`,
      { unitName, customerName, reason }
    );
  },

  containerCreated: (containerNumber, destination, createdBy) => {
    Notifications.add(
      NotificationTypes.CONTAINER_CREATED,
      'Container Created',
      `Container ${containerNumber} created for ${destination} by ${createdBy}`,
      { containerNumber, destination, createdBy }
    );
  },

  containerLoaded: (containerNumber, unitCount, loadedBy) => {
    Notifications.add(
      NotificationTypes.CONTAINER_LOADED,
      'Container Loaded',
      `${unitCount} unit(s) loaded into ${containerNumber} by ${loadedBy}`,
      { containerNumber, unitCount, loadedBy }
    );
  },

  containerArrived: (containerNumber, markedBy) => {
    Notifications.add(
      NotificationTypes.CONTAINER_ARRIVED,
      'Container Arrived',
      `Container ${containerNumber} marked as arrived by ${markedBy}`,
      { containerNumber, markedBy }
    );
  },

  requestNew: (customerName, requestType) => {
    Notifications.add(
      NotificationTypes.REQUEST_NEW,
      'New Request',
      `${customerName} submitted a new ${requestType} request`,
      { customerName, requestType }
    );
  },

  priceUpdated: (unitName, updatedBy) => {
    Notifications.add(
      NotificationTypes.PRICE_UPDATED,
      'Price Updated',
      `Pricing updated for ${unitName} by ${updatedBy}`,
      { unitName, updatedBy }
    );
  },

  customerAdded: (customerName, addedBy) => {
    Notifications.add(
      NotificationTypes.CUSTOMER_ADDED,
      'New Customer',
      `${customerName} was added by ${addedBy}`,
      { customerName, addedBy }
    );
  },

  userLogin: (userName, role) => {
    Notifications.add(
      NotificationTypes.USER_LOGIN,
      'User Login',
      `${userName} (${role}) logged in`,
      { userName, role }
    );
  },

  customerSettingsChanged: (customerName, changes) => {
    Notifications.add(
      'customer_settings',
      'Customer Settings Changed',
      `${customerName} updated their settings: ${changes}`,
      { customerName, changes }
    );
  },

  priceQuoteRequested: (customerName, unitSku, unitDescription) => {
    Notifications.add(
      'price_updated',
      'Price Quote Requested',
      `${customerName} is requesting FOB quote for ${unitSku} — ${unitDescription}`,
      { customerName, unitSku, unitDescription }
    );
  },

  priceQuoteSent: (unitSku, unitDescription, fobPrice, customerId) => {
    Notifications.add(
      'price_updated',
      'Price Quote Ready',
      `Your price quote is ready — ${unitDescription} FOB: $${fobPrice.toLocaleString()}`,
      { unitSku, fobPrice },
      customerId
    );
  },

  // Invoice sent to customer — translated
  invoiceSent: (message, customerName, sentBy, customerId) => {
    if (customerId) {
      Notifications.addForCustomer('invoice_created', 'invoice_sent', { name: customerName, _fallbackTitle: 'New Invoice', _fallbackMessage: message }, customerId);
    }
    Notifications.add('invoice_created', 'Invoice Sent', `${sentBy} sent invoice to ${customerName}`, { customerName, sentBy });
  },

  // Invoice with full details — translated
  invoiceSentToCustomer: (invoiceNumber, total, dueDate, customerId) => {
    const customers = JSON.parse(localStorage.getItem('bigstar_customers') || '[]');
    const cust = customers.find(c => c.id === customerId);
    if (customerId) {
      Notifications.addForCustomer('invoice_created', 'invoice_sent', {
        name: cust?.name || '', number: invoiceNumber, amount: total?.toLocaleString() || '0',
        currency: cust?.currency || 'USD', date: dueDate || '', milestone_amount: total?.toLocaleString() || '0',
        _fallbackTitle: 'Invoice Ready', _fallbackMessage: `Invoice ${invoiceNumber} — $${(total || 0).toLocaleString()} — due ${dueDate || 'upon receipt'}`,
      }, customerId);
    }
  },

  // Payment verified — translated
  paymentVerified: (message, customerName, verifiedBy, customerId) => {
    if (customerId) {
      Notifications.addForCustomer('payment_received', 'payment_confirmed', {
        name: customerName, _fallbackTitle: 'Payment Confirmed', _fallbackMessage: message,
      }, customerId);
    }
    Notifications.add('payment_received', 'Payment Verified', message, { customerName, verifiedBy });
  },

  // Payment rejected — translated
  paymentRejected: (message, customerName, rejectedBy, customerId) => {
    if (customerId) {
      Notifications.addForCustomer('payment_received', 'payment_overdue', {
        name: customerName, _fallbackTitle: 'Payment Not Verified', _fallbackMessage: message,
      }, customerId);
    }
    Notifications.add('payment_received', 'Payment Rejected', message, { customerName, rejectedBy });
  },

  // Container shipped/sealed — translated
  containerShipped: (message, customerName, shippedBy, customerId) => {
    if (customerId) {
      Notifications.addForCustomer('container_shipped', 'container_shipped', {
        name: customerName, _fallbackTitle: 'Container Shipped', _fallbackMessage: message,
      }, customerId);
    }
    Notifications.add('container_shipped', 'Container Sealed', message, { customerName, shippedBy });
  },

  // ── Selection Orders ── translated customer notifications
  selectionApproved: (orderId, customerName, unitCount, customerId) => {
    Notifications.addForCustomer('unit_assigned', 'selection_approved', {
      name: customerName, count: unitCount, _fallbackTitle: 'Selection Approved',
      _fallbackMessage: `Your selection of ${unitCount} units approved`,
    }, customerId);
    // Staff notification (English)
    Notifications.add('unit_assigned', 'Selection Approved', `${customerName}'s selection #${orderId} approved — ${unitCount} units`, {});
  },
  selectionRejected: (orderId, customerName, customerId) => {
    Notifications.addForCustomer('unit_assigned', 'selection_approved', {
      name: customerName, count: 0, _fallbackTitle: 'Selection Reviewed',
      _fallbackMessage: `Your selection order has been reviewed — some units were not available`,
    }, customerId);
  },
  selectionPartial: (orderId, customerName, approved, total, customerId) => {
    Notifications.addForCustomer('unit_assigned', 'selection_approved', {
      name: customerName, count: approved, _fallbackTitle: 'Selection Partially Approved',
      _fallbackMessage: `${approved} of ${total} units confirmed`,
    }, customerId);
    Notifications.add('unit_assigned', 'Selection Partial', `${customerName}'s selection #${orderId} — ${approved}/${total} approved`, {});
  },

  // ── Invoices ──
  invoiceCreated: (invoiceNumber, customerName, total, createdBy) => {
    Notifications.add('invoice_created', 'Invoice Created', `Invoice ${invoiceNumber} created for ${customerName} — $${total.toLocaleString()} by ${createdBy}`, {});
  },

  // ── Containers ──
  containerStatusChanged: (containerNumber, newStatus, customerIds) => {
    const msgs = {
      booked: `Container ${containerNumber} has been booked`,
      loading: `Container ${containerNumber} is now loading`,
      shipped: `Your container ${containerNumber} has departed Japan`,
      in_transit: `Container ${containerNumber} is in transit`,
      at_port: `Container ${containerNumber} has arrived at port`,
      customs: `Container ${containerNumber} is in customs clearance`,
      delivered: `Container ${containerNumber} has been delivered`,
    };
    const msg = msgs[newStatus] || `Container ${containerNumber} status: ${newStatus}`;
    Notifications.add('container_shipped', 'Container Update', msg, { containerNumber, newStatus });
    (customerIds || []).forEach(custId => {
      Notifications.add('container_shipped', 'Container Update', msg, { containerNumber }, custId);
    });
  },
  containerTimelineEvent: (containerNumber, note, customerIds) => {
    Notifications.add('container_shipped', 'Container Update', `Update on container ${containerNumber}: ${note}`, {});
    (customerIds || []).forEach(custId => {
      Notifications.add('container_shipped', 'Shipment Update', `Update on your container ${containerNumber}: ${note}`, {}, custId);
    });
  },

  // ── Cut Plans ──
  cutPlanCreated: (planId, vehicleName, createdBy) => {
    Notifications.add('cut_plan_approval_needed', 'Cut Plan Created', `Cut plan ${planId} for ${vehicleName} created by ${createdBy} — review needed`, {});
  },
  cutPlanApproved: (planId, vehicleName, approvedBy) => {
    Notifications.add('cut_plan_approved', 'Cut Plan Approved', `Cut plan for ${vehicleName} approved by ${approvedBy} — ready for cutting`, {});
  },
  cutPlanCompleted: (planId, vehicleName, customerIds) => {
    Notifications.add('cut_plan_approved', 'Cut Plan Complete', `All parts extracted from ${vehicleName}`, {});
    (customerIds || []).forEach(custId => {
      Notifications.addForCustomer('cut_plan_approved', 'parts_ready', {
        vehicle: vehicleName, count: '', _fallbackTitle: 'Parts Ready',
        _fallbackMessage: `Your requested parts from ${vehicleName} are ready`,
      }, custId);
    });
  },

  // ── Requests ──
  requestStatusChanged: (requestId, newStatus, customerName, customerId) => {
    const msgs = { processing: 'is being reviewed', sourced: 'has been sourced', quoted: 'has been quoted', completed: 'is complete' };
    const msg = msgs[newStatus] || `status updated to ${newStatus}`;
    if (customerId) Notifications.add('request_update', 'Request Update', `Your request #${requestId} ${msg}`, {}, customerId);
    Notifications.add('request_updated', 'Request Update', `Request #${requestId} from ${customerName} ${msg}`, {});
  },
  requestReplied: (requestId, replierName, isStaff, targetId) => {
    if (isStaff && targetId) {
      Notifications.add('request_update', 'New Reply', `New reply on your request #${requestId}`, {}, targetId);
    } else {
      Notifications.add('request_updated', 'Customer Reply', `${replierName} replied on request #${requestId}`, {});
    }
  },

  // ── Tasks ──
  taskAssigned: (title, assigneeName, dueDate) => {
    Notifications.add('general', 'New Task', `New task: ${title} — assigned to ${assigneeName}${dueDate ? ` — Due: ${dueDate}` : ''}`, {});
  },
  taskCompleted: (title, completedBy) => {
    Notifications.add('general', 'Task Completed', `Task completed: ${title} by ${completedBy}`, {});
  },
  taskOverdue: (title, assigneeName) => {
    Notifications.add('general', 'Task Overdue', `Task overdue: ${title} assigned to ${assigneeName}`, {});
  },

  // ── Staff ──
  staffAdded: (staffName, role, addedBy) => {
    Notifications.add('general', 'New Staff Member', `${staffName} (${role}) added by ${addedBy}`, {});
  },

  // ── Customer stock ──
  unitAddedToCustomerStock: (unitName, customerName, addedBy, customerId) => {
    Notifications.add('unit_assigned', 'Unit Added to Stock', `A new unit has been added to your stock: ${unitName}`, {}, customerId);
    Notifications.add('unit_assigned', 'Stock Updated', `${unitName} added to ${customerName}'s stock by ${addedBy}`, {});
  },

  // ── Damage ──
  damageReported: (unitSku, unitName, customerName, condition, customerId) => {
    Notifications.add('damage_reported', 'DAMAGE REPORTED', `${customerName} reported ${condition.replace(/_/g, ' ')} on ${unitName} (${unitSku})`, {});
  },

  blStatusChanged: (containerNumber, newStatus, customerIds, destination) => {
    const customerMessages = {
      draft_received: `Draft Bill of Lading is available for your container ${containerNumber}. Log in to view and download. If you need any amendments, please let us know.`,
      revised: `Revised Bill of Lading uploaded for container ${containerNumber}. Please review the updated document.`,
      finalized: `Bill of Lading finalized for container ${containerNumber}. Complete your final payment to receive the BL.`,
      surrendered: `BL for container ${containerNumber} has been released. You can now clear your shipment${destination ? ` at ${destination}` : ''}. Download the BL from your portal.`,
    };
    const staffMessages = {
      draft_received: `Draft BL uploaded for ${containerNumber}`,
      revised: `Revised BL uploaded for ${containerNumber}`,
      finalized: `BL finalized for ${containerNumber}`,
      surrendered: `BL surrendered for ${containerNumber}`,
    };
    const staffMsg = staffMessages[newStatus];
    const custMsg = customerMessages[newStatus];
    if (staffMsg) Notifications.add('container_arrived', 'BL Update', staffMsg, { containerNumber, newStatus });
    // Customer notifications — translated
    const templateMap = { draft_received: 'bl_draft_available', finalized: 'bl_payment_required', surrendered: 'bl_surrendered' };
    const templateKey = templateMap[newStatus];
    if (templateKey) {
      (customerIds || []).forEach(custId => {
        Notifications.addForCustomer('container_arrived', templateKey, {
          container: containerNumber, destination: destination || '',
          _fallbackTitle: newStatus === 'surrendered' ? 'BL Released' : newStatus === 'finalized' ? 'BL Ready' : 'Draft BL Available',
          _fallbackMessage: custMsg || '',
        }, custId);
      });
    } else if (custMsg) {
      // Revised or other — no template, use English
      (customerIds || []).forEach(custId => {
        Notifications.add('container_arrived', 'BL Update', custMsg, { containerNumber }, custId);
      });
    }
  },
};
